import { NgTemplateOutlet } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Input, Output, ViewChild, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { ColumnDefinition } from './column-definition';

/**
 * Elenco paginato.
 *
 * Prende il posto di `ngx-dynamic-table` mantenendone l'interfaccia
 * (`columnDefinitions`, `populate()`, `paginator`, `sort`, `pageChange`,
 * `pageSizeChange`, `sortChange`, `onInit`), cosi' le schermate si migrano
 * cambiando un import. In cambio si ottiene quello che dall'esterno non si
 * poteva aggiungere: intestazione appiccicata allo scroll, righe cliccabili,
 * stato vuoto e stato di errore distinti, righe finte durante l'attesa, e su
 * schermo stretto righe che diventano schede leggibili.
 */
@Component({
  selector: 'app-data-table',
  standalone: true,
  imports: [NgTemplateOutlet, MatTableModule, MatSortModule, MatPaginatorModule, MatIconModule],
  templateUrl: './data-table.component.html',
})
export class DataTableComponent<T> implements AfterViewInit {
  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  @Input() columnDefinitions: ColumnDefinition<T>[] = [];
  @Input() pageSizeOptions: number[] = [10, 25, 50, 100];
  @Input() showFirstLastButtons = true;
  @Input() noElementsMessage = 'Nessun elemento presente';

  /** Righe finte al posto del contenuto, mentre i dati arrivano. */
  @Input() caricamento = false;
  /** Motivo del guasto. Prende il posto dello stato vuoto: sono cose diverse. */
  @Input() errore: string | null = null;
  @Input() rigaCliccabile = false;
  @Input() etichettaVuoto: string | null = null;

  @Output() pageChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();
  @Output() sortChange = new EventEmitter<Sort>();
  // Nome storico: le schermate lo usano per lanciare la prima ricerca quando
  // paginatore e ordinamento esistono.
  @Output() onInit = new EventEmitter<void>();
  @Output() rigaScelta = new EventEmitter<T>();

  readonly dataSource = new MatTableDataSource<T>([]);
  readonly popolata = signal(false);

  /** Righe finte da disegnare durante l'attesa. */
  readonly scheletro = [0, 1, 2, 3, 4, 5];

  private dimensioneCorrente = 0;

  ngAfterViewInit(): void {
    this.dimensioneCorrente = this.paginator.pageSize;
    this.onInit.emit();
  }

  get displayedColumns(): string[] {
    return this.columnDefinitions.map((c) => String(c.id));
  }

  get vuota(): boolean {
    return this.popolata() && !this.caricamento && !this.errore && this.dataSource.data.length === 0;
  }

  populate(result: { items: T[]; total: number }): void {
    this.dataSource.data = result.items;
    // Il paginatore e' `static: true`, quindi esiste gia' qui: la versione
    // precedente lo attendeva con un setInterval da 200ms, e il totale
    // compariva in ritardo.
    this.paginator.length = result.total;
    this.popolata.set(true);
  }

  intestazione(colonna: ColumnDefinition<T>): string {
    return colonna.header ?? '';
  }

  etichettaCella(colonna: ColumnDefinition<T>): string {
    return colonna.etichettaMobile ?? colonna.header ?? '';
  }

  valore(riga: T, colonna: ColumnDefinition<T>): string {
    if (colonna.id === '_') return '';
    const grezzo = (riga as Record<string, unknown>)[colonna.id as string];
    if (colonna.parser) return colonna.parser(grezzo) ?? '';
    return grezzo === null || grezzo === undefined ? '' : String(grezzo);
  }

  classeAllineamento(colonna: ColumnDefinition<T>): string {
    switch (colonna.allineamento) {
      case 'fine':
        return 'tabella__cella--fine';
      case 'centro':
        return 'tabella__cella--centro';
      default:
        return '';
    }
  }

  /**
   * Il paginatore di Material manda un solo evento; le schermate ne aspettano
   * due, perche' cambiare quante righe vedere significa anche ricominciare
   * dalla prima pagina.
   */
  cambiaPagina(evento: PageEvent): void {
    if (evento.pageSize !== this.dimensioneCorrente) {
      this.dimensioneCorrente = evento.pageSize;
      this.pageSizeChange.emit(evento.pageSize);
      return;
    }
    this.pageChange.emit(evento.pageIndex);
  }

  scegliRiga(riga: T): void {
    if (this.rigaCliccabile) this.rigaScelta.emit(riga);
  }
}
